import React, { useState, useMemo } from "react";
import {
    AlertTriangle,
    CheckCircle2,
    XCircle,
    AlertCircle,
    ChevronDown,
    X,
    Search,
    ListTree,
    ArrowRight
} from "lucide-react";
import "./RulesValidationModalV2.css";

const SEVERITY_CONFIG = {
    FATAL: { icon: XCircle, color: "#dc2626", class: "fatal", label: "Fatal" },
    WARNING: { icon: AlertTriangle, color: "#d97706", class: "warning", label: "Warning" },
    PASS: { icon: CheckCircle2, color: "#16a34a", class: "pass", label: "Passed" },
    ERROR: { icon: AlertCircle, color: "#6b7280", class: "error", label: "Error" },
};

function RuleCardV2({ rule }) {
    const [expanded, setExpanded] = useState(false);
    const config = SEVERITY_CONFIG[rule.severity] || SEVERITY_CONFIG.ERROR;
    const Icon = config.icon;

    const isResultFalse = (val) => String(val).toLowerCase() === "false";

    return (
        <div className="rv2-rule-card">
            <div className={`rv2-rule-bar ${config.class}`} />
            <div className="rv2-rule-body">
                <div className="rv2-rule-top">
                    <div className="rv2-rule-main">
                        <span className="rv2-rule-name">{rule.ruleName}</span>
                        <span className="rv2-rule-msg">{rule.message}</span>
                        {rule.comparison && (
                            <div className="rv2-rule-meta">
                                <span>Program</span>
                                <strong>{rule.comparison.program || "—"}</strong>
                            </div>
                        )}
                    </div>
                    <div className="rv2-rule-right">
                        <span className={`rv2-severity-badge ${config.class}`}>{rule.severity}</span>
                        {rule.trace && rule.trace.length > 0 && (
                            <button className="rv2-trace-btn" onClick={() => setExpanded(!expanded)}>
                                View trace
                            </button>
                        )}
                        <button
                            className={`rv2-expand-btn ${expanded ? "expanded" : ""}`}
                            onClick={() => setExpanded(!expanded)}
                        >
                            <ChevronDown size={12} />
                        </button>
                    </div>
                </div>

                {expanded && rule.trace && rule.trace.length > 0 && (
                    <div className="rv2-trace">
                        <div className="rv2-trace-title">
                            <ListTree size={12} />
                            Evaluation Trace
                        </div>
                        <table className="rv2-trace-table">
                            <thead>
                                <tr>
                                    <th style={{ width: "50px" }}>Step</th>
                                    <th>Detail</th>
                                    <th style={{ width: "120px" }}>Value</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rule.trace.map((t, idx) => (
                                    <tr key={idx}>
                                        <td>{t.step}</td>
                                        <td>{t.description}</td>
                                        <td className={`rv2-trace-val ${isResultFalse(t.value) ? "fail" : ""} ${String(t.value).toLowerCase() === "true" ? "pass" : ""}`}>
                                            {t.value}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

export default function RulesValidationModalV2({ isOpen, result, onClose, onContinueSave }) {
    const defaultTab = useMemo(() => {
        const s = result?.summary;
        if (!s) return "all";
        if (s.fatalCount > 0) return "fatal";
        if (s.warningCount > 0) return "warning";
        return "all";
    }, [result]);

    const [activeTab, setActiveTab] = useState(defaultTab);
    const [searchText, setSearchText] = useState("");

    // Sync activeTab when result changes (modal re-opens with new data)
    React.useEffect(() => {
        setActiveTab(defaultTab);
    }, [defaultTab]);

    const allRules = useMemo(() => {
        if (!result) return [];
        const rules = [];
        (result.groups || []).forEach(g => {
            (g.rules || []).forEach(r => rules.push({ ...r, groupName: g.groupName, program: g.program }));
        });
        return rules;
    }, [result]);

    const filteredRules = useMemo(() => {
        let rules = allRules;
        if (activeTab === "fatal") rules = rules.filter(r => r.severity === "FATAL");
        else if (activeTab === "warning") rules = rules.filter(r => r.severity === "WARNING");
        else if (activeTab === "passed") rules = rules.filter(r => r.severity === "PASS");
        else if (activeTab === "error") rules = rules.filter(r => r.severity === "ERROR");

        if (searchText.trim()) {
            const q = searchText.toLowerCase();
            rules = rules.filter(r =>
                (r.ruleName || "").toLowerCase().includes(q) ||
                (r.message || "").toLowerCase().includes(q)
            );
        }
        return rules;
    }, [allRules, activeTab, searchText]);

    const groupedRules = useMemo(() => {
        const map = new Map();
        filteredRules.forEach(r => {
            const key = r.groupName || "Ungrouped";
            if (!map.has(key)) map.set(key, { groupName: key, program: r.program, rules: [] });
            map.get(key).rules.push(r);
        });
        return [...map.values()];
    }, [filteredRules]);

    if (!isOpen || !result) return null;

    const summary = result.summary || { passCount: 0, warningCount: 0, fatalCount: 0, errorCount: 0 };
    const totalRules = summary.passCount + summary.warningCount + summary.fatalCount + summary.errorCount;
    const totalIssues = summary.fatalCount + summary.warningCount + summary.errorCount;

    const progressPercent = totalRules > 0
        ? { pass: (summary.passCount / totalRules) * 100, warning: (summary.warningCount / totalRules) * 100, fatal: (summary.fatalCount / totalRules) * 100, error: (summary.errorCount / totalRules) * 100 }
        : { pass: 0, warning: 0, fatal: 0, error: 0 };

    // Build smart summary message
    const buildSummaryMessage = () => {
        if (result.overallStatus === "PASS") {
            return { type: "pass", bold: "All rules passed.", rest: "No issues found." };
        }
        if (result.overallStatus === "FATAL") {
            return { type: "fatal", bold: "Blocking issues found.", rest: `${summary.fatalCount} fatal issue(s) must be resolved before saving.` };
        }
        // WARNING or mixed
        const parts = [];
        if (summary.warningCount > 0) parts.push(`${summary.warningCount} warnings need review`);

        // Check if many warnings share same message (common cause)
        const msgCounts = {};
        allRules.filter(r => r.severity === "WARNING").forEach(r => {
            const m = r.message || "";
            msgCounts[m] = (msgCounts[m] || 0) + 1;
        });
        const topMsg = Object.entries(msgCounts).sort((a, b) => b[1] - a[1])[0];
        let causeNote = "";
        if (topMsg && topMsg[1] > 1 && topMsg[1] >= summary.warningCount * 0.5) {
            causeNote = ` — ${topMsg[1]} come from one cause: ${topMsg[0].toLowerCase().substring(0, 80)}`;
        }

        return {
            type: "warning",
            bold: "No blocking issues.",
            rest: `${parts.join(". ")}${causeNote}. You can still save.`
        };
    };

    const summaryMsg = buildSummaryMessage();

    // Context subtitle
    const contextName = (result.groups || [])[0]?.groupName || "";
    const programName = (result.groups || [])[0]?.program || "";

    const openWarnings = summary.warningCount + summary.fatalCount;

    return (
        <div className="rv2-overlay">
            <div className="rv2-modal">
                {/* Header */}
                <div className="rv2-header">
                    <div>
                        <h2 className="rv2-title">Rule validation results</h2>
                        <p className="rv2-subtitle">

                            {programName && <>Program <strong>{programName}</strong> · </>}
                            {totalRules} rules executed
                        </p>
                    </div>
                    <button className="rv2-close-btn" onClick={onClose}>
                        <X size={18} />
                    </button>
                </div>

                {/* Progress Bar */}
                <div className="rv2-progress-bar">
                    {summary.passCount > 0 && <div className="rv2-progress-seg pass" style={{ width: `${progressPercent.pass}%` }} />}
                    {summary.warningCount > 0 && <div className="rv2-progress-seg warning" style={{ width: `${progressPercent.warning}%` }} />}
                    {summary.fatalCount > 0 && <div className="rv2-progress-seg fatal" style={{ width: `${progressPercent.fatal}%` }} />}
                    {summary.errorCount > 0 && <div className="rv2-progress-seg error" style={{ width: `${progressPercent.error}%` }} />}
                </div>

                {/* Filter Tabs */}
                <div className="rv2-filters">
                    <button className={`rv2-filter-pill ${activeTab === "all" ? "active" : ""}`} onClick={() => setActiveTab("all")}>
                        All issues <span className="rv2-pill-count">{totalRules}</span>
                    </button>
                    <button className={`rv2-filter-pill fatal ${activeTab === "fatal" ? "active" : ""}`} onClick={() => setActiveTab("fatal")}>
                        <span className="rv2-pill-dot fatal" /> Fatal <span className="rv2-pill-count">{summary.fatalCount}</span>
                    </button>
                    <button className={`rv2-filter-pill warning ${activeTab === "warning" ? "active" : ""}`} onClick={() => setActiveTab("warning")}>
                        <span className="rv2-pill-dot warning" /> Warning <span className="rv2-pill-count">{summary.warningCount}</span>
                    </button>
                    <button className={`rv2-filter-pill pass ${activeTab === "passed" ? "active" : ""}`} onClick={() => setActiveTab("passed")}>
                        <span className="rv2-pill-dot pass" /> Passed <span className="rv2-pill-count">{summary.passCount}</span>
                    </button>
                    <button className={`rv2-filter-pill error ${activeTab === "error" ? "active" : ""}`} onClick={() => setActiveTab("error")}>
                        <span className="rv2-pill-dot error" /> Error <span className="rv2-pill-count">{summary.errorCount}</span>
                    </button>
                </div>

                {/* Summary Banner */}
                {!result.errorMessage && (
                    <div className={`rv2-banner ${summaryMsg.type}`}>
                        <AlertTriangle size={16} />
                        <span><strong>{summaryMsg.bold}</strong> {summaryMsg.rest}</span>
                    </div>
                )}

                {result.errorMessage && (
                    <div className="rv2-banner fatal">
                        <AlertCircle size={16} />
                        <span>{result.errorMessage}</span>
                    </div>
                )}

                {/* Search */}
                <div className="rv2-search-row">
                    <div className="rv2-search-box">
                        <Search size={14} className="rv2-search-icon" />
                        <input
                            type="text"
                            placeholder="Search rules..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            className="rv2-search-input"
                        />
                    </div>
                    <span className="rv2-search-count">Showing {filteredRules.length} of {totalRules} rules</span>
                </div>

                {/* Body */}
                <div className="rv2-body">
                    {groupedRules.map((group, gIdx) => (
                        <div key={gIdx} className="rv2-group">
                            <div className="rv2-group-header">
                                <span className="rv2-group-name">{group.groupName}</span>
                                {group.program && (
                                    <span className="rv2-group-program">{group.program}</span>
                                )}
                            </div>
                            <div className="rv2-group-rules">
                                {group.rules.map((rule, rIdx) => (
                                    <RuleCardV2 key={rIdx} rule={rule} />
                                ))}
                            </div>
                        </div>
                    ))}

                    {filteredRules.length === 0 && (
                        <div className="rv2-empty">No rules match the current filter.</div>
                    )}
                </div>

                {/* Footer */}
                <div className="rv2-footer">
                    <div className="rv2-footer-info">
                        Saving records the final determination with <strong>{openWarnings} open warning{openWarnings !== 1 ? "s" : ""}</strong>.

                    </div>
                    <div className="rv2-footer-actions">
                        <button className="rv2-btn-cancel" onClick={onClose}>Cancel</button>
                        <button
                            className="rv2-btn-save"
                            onClick={onContinueSave}
                            disabled={result.overallStatus === "FATAL"}
                        >
                            Acknowledge & save <ArrowRight size={14} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
